/**
 * statusTaxonomy.ts
 *
 * Two-layer domain status model for LL97 / LL88 filings.
 *
 * LAYER 1 (glanceable): every raw backend status code maps to exactly one
 *   bucket + ownership + tone. Used by <StatusBadge /> and <OwnershipPill />
 *   on dashboards, tables, headers, and filing rows.
 *
 * LAYER 2 (audit): the raw (code, label) pair is preserved verbatim. The
 *   <StatusHistoryDrawer /> renders the full sequence of raw transitions so
 *   RDPs and staff always see the exact DOB / BEAM state the backend recorded.
 *   We never collapse distinct codes into a shared label in Layer 2.
 *
 * Devil's advocate notes baked in:
 *   - 6 buckets, not 4 or 8. 4 loses signal ("With RDP" vs "With DOB" is the
 *     single most-asked support question — owners need to know who's holding
 *     the ball). 8 fragments the summary tiles without adding clarity.
 *   - 9999 "Outside Carbon Clear" IS shown to owners — they already know we
 *     don't own the filing, hiding it would be worse (they'd think they have
 *     an open task). Bucket: "exception".
 *   - 460 "Sign Owner Attestation" is "action_needed" for owner — it's a
 *     task that blocks progression, not an exception.
 *   - 9001-9004 exemptions are "complete" in the sense the owner has nothing
 *     to do, but tone is "info" (not success), because this is a regulatory
 *     carve-out not an accepted filing.
 *
 * This module is pure data. No JSX. JSX that used to live in
 *   constants/statuses/ll97.tsx (the `content` field) must stay out of here
 *   and be rendered by the consumer component that has the click handlers.
 */

import {
  AlertTriangle,
  CheckCircle2,
  CircleDashed,
  Clock,
  FileSignature,
  Gavel,
  HelpCircle,
  Inbox,
  Landmark,
  ShieldCheck,
  UserCheck,
  XCircle,
  type LucideIcon,
} from "lucide-react";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Jurisdiction = "ll97" | "ll88";

export type StatusBucket =
  | "action_needed" // Owner or staff must act before filing can progress.
  | "in_review" // Carbon Clear internal review / QA.
  | "with_rdp" // Sitting with the RDP for attestation / signature.
  | "with_dob" // Submitted; awaiting NYC DOB / BEAM response.
  | "complete" // Accepted, exempt-and-closed, or terminal success.
  | "exception"; // Rejected, out-of-scope, filing-elsewhere, unknown.

export type Ownership = "owner" | "rdp" | "dob" | "none";

export type BadgeTone = "success" | "warning" | "danger" | "info" | "neutral";

export interface StatusDisplay {
  /** Raw backend status code (preserved verbatim for audit). */
  code: number;
  /** Original human label exactly as it appeared in constants/statuses/*.tsx. */
  label: string;
  /** Short, glanceable word used by <StatusBadge compact />. */
  shortLabel: string;
  /** Layer-1 bucket for grouping in dashboards and summary tiles. */
  bucket: StatusBucket;
  /** Who is blocking progression? Drives the "Waiting on: X" pill. */
  ownership: Ownership;
  /** Semantic color tone. Maps to <Badge variant="..."> in SWE-1's primitive. */
  tone: BadgeTone;
  /** Lucide icon — always rendered alongside the word. Never icon-only. */
  icon: LucideIcon;
}

// ---------------------------------------------------------------------------
// Bucket metadata (for summary tiles / dashboard headers)
// ---------------------------------------------------------------------------

export const BUCKET_META: Record<
  StatusBucket,
  { label: string; tone: BadgeTone; icon: LucideIcon }
> = {
  action_needed: { label: "Action needed from you", tone: "warning", icon: AlertTriangle },
  in_review: { label: "In review", tone: "info", icon: Clock },
  with_rdp: { label: "With RDP", tone: "info", icon: UserCheck },
  with_dob: { label: "With DOB", tone: "info", icon: Landmark },
  complete: { label: "Complete", tone: "success", icon: CheckCircle2 },
  exception: { label: "Exception / Other", tone: "neutral", icon: HelpCircle },
};

// ---------------------------------------------------------------------------
// LL97 taxonomy — one entry per code known to the backend.
// Labels are copied verbatim from constants/statuses/ll97.tsx (do NOT edit).
// ---------------------------------------------------------------------------

const LL97: Record<number, Omit<StatusDisplay, "code">> = {
  0:    { label: "Not Started",                    shortLabel: "Not Started",      bucket: "action_needed", ownership: "owner", tone: "warning", icon: CircleDashed },
  100:  { label: "Add BIN",                        shortLabel: "Add BIN",          bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  200:  { label: "Connect Portfolio Manager",      shortLabel: "Connect ESPM",     bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  210:  { label: "Verify ESPM Data",               shortLabel: "Verify ESPM",      bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  310:  { label: "Complete Questionnaire",         shortLabel: "Questionnaire",    bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  311:  { label: "Complete Questionnaire",         shortLabel: "Questionnaire",    bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  320:  { label: "Complete Questionnaire",         shortLabel: "Questionnaire",    bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  321:  { label: "Complete Questionnaire",         shortLabel: "Questionnaire",    bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  350:  { label: "Portfolio Manager QA",           shortLabel: "ESPM QA",          bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  400:  { label: "Upload Files",                   shortLabel: "Upload Files",     bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  422:  { label: "Upload Decarbonization Plans",   shortLabel: "Upload Decarb",    bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  423:  { label: "Upload Energy Audit Files",      shortLabel: "Upload Audit",     bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  434:  { label: "Upload GFA Documents",           shortLabel: "Upload GFA",       bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  441:  { label: "Upload ECM Documents",           shortLabel: "Upload ECM",       bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  444:  { label: "Upload ECM Documents",           shortLabel: "Upload ECM",       bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  460:  { label: "Sign Owner Attestation",         shortLabel: "Sign",             bucket: "action_needed", ownership: "owner", tone: "warning", icon: FileSignature },
  501:  { label: "GFA Documentation Assistance",      shortLabel: "GFA Assist",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  502:  { label: "GFA Documentation Assistance Paid", shortLabel: "GFA Assist Paid",  bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  503:  { label: "ECM Documentation Assistance",      shortLabel: "ECM Assist",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  504:  { label: "ECM Documentation Assistance Paid", shortLabel: "ECM Assist Paid",  bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  510:  { label: "Site Visit Pending",             shortLabel: "Site Visit",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  511:  { label: "Site Visit Pending",             shortLabel: "Site Visit",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  512:  { label: "Site Visit Pending",             shortLabel: "Site Visit",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  513:  { label: "Site Visit Pending",             shortLabel: "Site Visit",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  520:  { label: "Decarbonization Plan",            shortLabel: "Decarb Plan",      bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  550:  { label: "Awaiting RDP Assignment",        shortLabel: "RDP Assign",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  700:  { label: "Verification",                   shortLabel: "Verifying",        bucket: "in_review", ownership: "none", tone: "info", icon: ShieldCheck },
  800:  { label: "Awaiting RDP Review",            shortLabel: "With RDP",         bucket: "with_rdp", ownership: "rdp", tone: "info", icon: UserCheck },
  850:  { label: "Submission in Progress",         shortLabel: "Submitting",       bucket: "with_dob", ownership: "dob", tone: "info", icon: Inbox },
  900:  { label: "Submitted",                      shortLabel: "Submitted",        bucket: "with_dob", ownership: "dob", tone: "info", icon: Landmark },
  910:  { label: "Submitted",                      shortLabel: "Submitted",        bucket: "with_dob", ownership: "dob", tone: "info", icon: Landmark },
  911:  { label: "Submitted",                      shortLabel: "Submitted",        bucket: "with_dob", ownership: "dob", tone: "info", icon: Landmark },
  915:  { label: "Deficient Response Requested",   shortLabel: "Deficient",        bucket: "action_needed", ownership: "owner", tone: "danger", icon: AlertTriangle },
  916:  { label: "Deficient Responded",            shortLabel: "Responded",        bucket: "with_dob", ownership: "dob", tone: "info", icon: Landmark },
  1000: { label: "Accepted",                       shortLabel: "Accepted",         bucket: "complete", ownership: "none", tone: "success", icon: CheckCircle2 },
  1100: { label: "Rejected",                       shortLabel: "Rejected",         bucket: "exception", ownership: "owner", tone: "danger", icon: XCircle },
  9001: { label: "Exempt until 2026",              shortLabel: "Exempt '26",       bucket: "complete", ownership: "none", tone: "info", icon: ShieldCheck },
  9002: { label: "Exempt until 2035",              shortLabel: "Exempt '35",       bucket: "complete", ownership: "none", tone: "info", icon: ShieldCheck },
  9003: { label: "City Portfolio Property - Exempt", shortLabel: "City Exempt",    bucket: "complete", ownership: "none", tone: "info", icon: ShieldCheck },
  9004: { label: "Not on CBL",                     shortLabel: "Not on CBL",       bucket: "complete", ownership: "none", tone: "info", icon: ShieldCheck },
  9050: { label: "Filing on Parent Property",      shortLabel: "Parent Filing",    bucket: "exception", ownership: "none", tone: "neutral", icon: HelpCircle },
  9051: { label: "Filing on Child Property",       shortLabel: "Child Filing",     bucket: "exception", ownership: "none", tone: "neutral", icon: HelpCircle },
  9999: { label: "Outside Carbon Clear",           shortLabel: "Outside CC",       bucket: "exception", ownership: "none", tone: "neutral", icon: HelpCircle },
};

// ---------------------------------------------------------------------------
// LL88 taxonomy — one entry per code known to the backend.
// Labels are copied verbatim from constants/statuses/ll88.tsx (do NOT edit).
// ---------------------------------------------------------------------------

const LL88: Record<number, Omit<StatusDisplay, "code">> = {
  0:    { label: "Not Started",                    shortLabel: "Not Started",      bucket: "action_needed", ownership: "owner", tone: "warning", icon: CircleDashed },
  100:  { label: "Submetering Questionnaire",      shortLabel: "Submetering Q",    bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  110:  { label: "Submetering Documentation",      shortLabel: "Submetering Docs", bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  150:  { label: "Submit Existing Attestations",   shortLabel: "Existing Attest",  bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  200:  { label: "Lighting Questionnaire",         shortLabel: "Lighting Q",       bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  210:  { label: "Lighting Documentation",         shortLabel: "Lighting Docs",    bucket: "action_needed", ownership: "owner", tone: "warning", icon: AlertTriangle },
  460:  { label: "Sign Owner Attestation",         shortLabel: "Sign",             bucket: "action_needed", ownership: "owner", tone: "warning", icon: FileSignature },
  510:  { label: "Site Visit in Progress",         shortLabel: "Site Visit",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  550:  { label: "RDP Assignment",                 shortLabel: "RDP Assign",       bucket: "in_review", ownership: "none", tone: "info", icon: Clock },
  700:  { label: "Verification",                   shortLabel: "Verifying",        bucket: "in_review", ownership: "none", tone: "info", icon: ShieldCheck },
  800:  { label: "Awaiting RDP Review",            shortLabel: "With RDP",         bucket: "with_rdp", ownership: "rdp", tone: "info", icon: UserCheck },
  850:  { label: "Submission in Progress",         shortLabel: "Submitting",       bucket: "with_dob", ownership: "dob", tone: "info", icon: Inbox },
  900:  { label: "Submitted",                      shortLabel: "Submitted",        bucket: "with_dob", ownership: "dob", tone: "info", icon: Landmark },
  1000: { label: "Accepted",                       shortLabel: "Accepted",         bucket: "complete", ownership: "none", tone: "success", icon: CheckCircle2 },
  1100: { label: "Rejected",                       shortLabel: "Rejected",         bucket: "exception", ownership: "owner", tone: "danger", icon: Gavel },
  9004: { label: "Not on CBL",                     shortLabel: "Not on CBL",       bucket: "complete", ownership: "none", tone: "info", icon: ShieldCheck },
  9050: { label: "Filing on Parent Property",      shortLabel: "Parent Filing",    bucket: "exception", ownership: "none", tone: "neutral", icon: HelpCircle },
  9051: { label: "Filing on Child Property",       shortLabel: "Child Filing",     bucket: "exception", ownership: "none", tone: "neutral", icon: HelpCircle },
  9999: { label: "Filed outside Carbon Clear",     shortLabel: "Outside CC",       bucket: "exception", ownership: "none", tone: "neutral", icon: HelpCircle },
};

// ---------------------------------------------------------------------------
// Known-code types — exported so consumers can narrow against accidental typos.
// Any code not in this union is still handled safely via the fallback below.
// ---------------------------------------------------------------------------

export type Ll97StatusCode = keyof typeof LL97;
export type Ll88StatusCode = keyof typeof LL88;

// ---------------------------------------------------------------------------
// Fallback — backend could ship a new code before the frontend ships support.
// Devil's advocate: do NOT render "Unknown" as success-green. Neutral tone,
// HelpCircle icon, exception bucket, no ownership attribution.
// ---------------------------------------------------------------------------

const UNKNOWN_DISPLAY = (code: number): StatusDisplay => ({
  code,
  label: `Status ${code}`,
  shortLabel: `Status ${code}`,
  bucket: "exception",
  ownership: "none",
  tone: "neutral",
  icon: HelpCircle,
});

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Resolve a raw backend status code into its display metadata.
 * Always returns a safe fallback — never throws, never returns undefined.
 */
export function getStatusDisplay(
  code: number,
  opts: { jurisdiction: Jurisdiction },
): StatusDisplay {
  const table = opts.jurisdiction === "ll97" ? LL97 : LL88;
  const hit = table[code];
  if (!hit) return UNKNOWN_DISPLAY(code);
  return { code, ...hit };
}

/** Convenience: who is the filing waiting on right now? */
export function deriveOwnership(
  code: number,
  opts: { jurisdiction: Jurisdiction },
): Ownership {
  return getStatusDisplay(code, opts).ownership;
}

/** Convenience: bucket-only lookup for dashboard summary tiles. */
export function getStatusBucket(
  code: number,
  opts: { jurisdiction: Jurisdiction },
): StatusBucket {
  return getStatusDisplay(code, opts).bucket;
}

/** All codes known for a jurisdiction — useful for dropdowns / filters. */
export function listStatusCodes(jurisdiction: Jurisdiction): number[] {
  return Object.keys(jurisdiction === "ll97" ? LL97 : LL88)
    .map(Number)
    .sort((a, b) => a - b);
}
